import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";
import { ColDef, SideBarDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Dropdown, Radio } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import CiqAgSearch from "components/ciq-ag-search";
import { buildPrintPayloadHash } from "./optimized-print-view/print-payload";
import { launchPrintView } from "./optimized-print-view/print-view-launcher";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import SubmittalMicroVizDateBlock from "components/submittal-details/submittal-microviz-dateblock";
import { truncatedTextCellRenderer } from "components/cell-renders";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import {
  useProjectParticipants,
  useIntegrationProjectParticipants
} from "hooks/project-participants";
import { isProjectInIntegrationMode } from "utils/utils";
import { SubmittalStatusToStrMap } from "../../../../constants";
import { SubmittalImpactDetailCellRenderer } from "./impact-detail-renderer";
import { TSubmittalGridRow, formatChangeInFloat } from "./grid-utils";
import { usePersistedExpansion } from "./use-persisted-expansion";
import {
  ALWAYS_EXPORT_KEYS_SUBMITTAL,
  SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_SUBMITTAL,
  SI_SUBMITTAL_RENAME_KEY,
  buildExportFileName,
  getVisibleExportColumnKeys,
  resolveSiHeader,
  scheduleImpactProcessCell
} from "./export-helpers";

type Props = {
  rowData: TSubmittalGridRow[];
  isLoading: boolean;
  printMode?: boolean;
  importLogId?: string;
  sourceLogId?: string;
  sourceScheduleName?: string;
  importScheduleName?: string;
};

function submittalIdInnerRenderer(params: any) {
  const { projectId } = params.context || {};
  return (
    <Link to={`/project/${projectId}/submittals/${params.data.submittal_id}`}>
      {params.value}
    </Link>
  );
}

function SubmittalsNoRowsOverlay({ context }: any) {
  const { isLoading: loading } = context || {};
  return (
    <span>
      {loading ? (
        <span className="loading-dots">Loading</span>
      ) : (
        "No Submittals are impacted."
      )}
    </span>
  );
}

const MemoMicroViz = memo(
  ({
    submittalData,
    projectParticipants,
    submittalRevisions,
    selectedRevision
  }: any) => (
    <SubmittalMicroVizDateBlock
      size="sm"
      submittalData={submittalData}
      projectParticipants={projectParticipants}
      submittalRevisions={submittalRevisions}
      selectedRevision={selectedRevision}
    />
  )
);
MemoMicroViz.displayName = "MemoMicroViz";

function submittalStatusRenderer(params: any) {
  const enrichment = params.data?._enrichment;
  const vizData = params.data?._vizSubmittalData;
  const sdbNew = params.data?._sdbNew;
  const { projectParticipants } = params.context || {};

  // Prefer enrichment data (from report API) when available
  if (enrichment && enrichment.date_block_submittals?.length > 0) {
    const statusText =
      SubmittalStatusToStrMap[enrichment.workflow_status] || "";
    return (
      <div className="flex flex-col justify-start leading-none">
        <div className="leading-normal">{statusText}</div>
        <div className="mt-1">
          <MemoMicroViz
            submittalData={enrichment}
            projectParticipants={projectParticipants}
            submittalRevisions={enrichment.revision_date_blocks}
            selectedRevision={enrichment.revision}
          />
        </div>
      </div>
    );
  }

  // Fallback: use pre-built stable submittalData from grid-utils
  if (vizData && sdbNew?.workflow_status_id != null) {
    const statusText = SubmittalStatusToStrMap[sdbNew.workflow_status_id] || "";
    return (
      <div className="flex flex-col justify-start leading-none">
        <div className="leading-normal">{statusText}</div>
        <div className="mt-1">
          <MemoMicroViz
            submittalData={vizData}
            projectParticipants={projectParticipants}
            submittalRevisions={vizData.revision_date_blocks}
            selectedRevision={vizData.revision}
          />
        </div>
      </div>
    );
  }

  // Last fallback: show the status text from grid data
  const { status } = params.data || {};
  if (!status) return null;
  return truncatedTextCellRenderer(params);
}

function riskCellRenderer(params: any) {
  const level = params.value;
  if (!level) return "";
  const config: Record<string, { label: string; color: string }> = {
    HIGH: { label: "High", color: "#dc2626" },
    MEDIUM: { label: "Medium", color: "#d97706" },
    LOW: { label: "Low", color: "#16a34a" },
    DELETED: { label: "Deleted", color: "#6b7280" }
  };
  const { label, color } = config[level] || {
    label: level,
    color: "#6b7280"
  };
  return (
    <span style={{ color, fontWeight: 600, fontSize: "12px" }}>{label}</span>
  );
}

function dateValueFormatter(params: any) {
  if (!params.value) return "";
  return DateUtils.format(params.value, DATE_FORMAT_MMDDYYYY) || "";
}

const RISK_CATEGORIES = [
  { key: "ALL", label: "All" },
  { key: "HIGH", label: "Attention Required" },
  { key: "DELETED", label: "Deleted Activity" }
];

function ImpactedSubmittalsGrid({
  rowData,
  isLoading,
  printMode,
  importLogId,
  sourceLogId,
  sourceScheduleName,
  importScheduleName
}: Props) {
  const gridRef = useRef<AgGridReact<any>>(null);
  const { projectId } = useParams() as any;
  const { expandedItemIds, setExpandedItemIds, toggleExpanded, expandedRef } =
    usePersistedExpansion("submittals", importLogId, sourceLogId);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  const { projectDetails, columnHeaders, isProjectArchived }: TProjectContext =
    useContext(ProjectContext);
  const submittalHeaderMap = columnHeaders?.submittalHeaderMap;
  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );
  const { projectParticipants: WFProjectParticipants } =
    useProjectParticipants(isIntegrationMode);
  const { data: INTGProjectParticipants } =
    useIntegrationProjectParticipants(!isIntegrationMode);
  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: rowData.length };
    rowData.forEach((r) => {
      counts[r.risk_level] = (counts[r.risk_level] || 0) + 1;
    });
    return counts;
  }, [rowData]);

  const filteredRowData = useMemo(
    () =>
      selectedCategory === "ALL"
        ? rowData
        : rowData.filter((r) => r.risk_level === selectedCategory),
    [rowData, selectedCategory]
  );

  const rowIds = useMemo(
    () => filteredRowData.map((r) => r.id),
    [filteredRowData]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"] as any,
      sortingOrder: ["asc", "desc"] as any,
      comparator: comparatorWithEmptyAtBottom,
      tooltipValueGetter: (params: any) => params.value
    }),
    []
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        colId: "submittal_sequence_id",
        field: "submittal_sequence_id",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "ID",
          SI_SUBMITTAL_RENAME_KEY.submittal_sequence_id
        ),
        width: 100,
        pinned: "left",
        lockPinned: true,
        lockPosition: true,
        lockVisible: true,
        suppressMovable: true,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: submittalIdInnerRenderer
        }
      },
      {
        colId: "title",
        field: "title",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Submittal Title",
          SI_SUBMITTAL_RENAME_KEY.title
        ),
        minWidth: 200,
        flex: 1
      },
      {
        colId: "spec_section",
        field: "spec_section",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Spec No",
          SI_SUBMITTAL_RENAME_KEY.spec_section
        ),
        width: 120
      },
      {
        colId: "spec_name",
        field: "spec_name",
        headerName: "Spec Name",
        width: 160,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "responsible_contractor",
        field: "responsible_contractor",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Responsible Contractor",
          SI_SUBMITTAL_RENAME_KEY.responsible_contractor
        ),
        width: 180,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "gc_reviewer",
        field: "gc_reviewer",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "GC Reviewer",
          SI_SUBMITTAL_RENAME_KEY.gc_reviewer
        ),
        width: 160,
        cellRenderer: truncatedTextCellRenderer,
        filter: "agSetColumnFilter"
      },
      {
        colId: "status",
        field: "status",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Status",
          SI_SUBMITTAL_RENAME_KEY.status
        ),
        width: 180,
        cellRenderer: submittalStatusRenderer
      },
      {
        colId: "linked_activity",
        field: "linked_activity",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Impacting Activity",
          SI_SUBMITTAL_RENAME_KEY.linked_activity
        ),
        width: 200,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "change_in_float",
        field: "change_in_float",
        headerName: "Change in Float",
        width: 150,
        valueFormatter: ({ value }: { value: number | null | undefined }) =>
          formatChangeInFloat(value),
        // Numeric — default string sort would put "+10" before "+2".
        comparator: (a: number | null, b: number | null) =>
          (a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY)
      },
      {
        colId: "risk_before",
        field: "risk_before",
        headerName: "Before Risk",
        width: 140,
        cellRenderer: riskCellRenderer,
        filter: "agSetColumnFilter",
        comparator: riskLevelComparator
      },
      {
        colId: "risk_after",
        field: "risk_after",
        headerName: "After Risk",
        width: 140,
        cellRenderer: riskCellRenderer,
        filter: "agSetColumnFilter",
        sort: "asc",
        comparator: riskLevelComparator
      },
      {
        colId: "old_activity_date",
        headerName: "Old Activity Date",
        valueGetter: (p: any) => p.data?._activity?.old_start ?? "",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "new_activity_date",
        headerName: "New Activity Date",
        valueGetter: (p: any) => p.data?._activity?.new_start ?? "",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "delta",
        field: "delta",
        headerName: "Delta",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "risk_assessment",
        field: "risk_assessment",
        headerName: "Risk Assessment after Schedule Change",
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        colId: "final_deadline",
        field: "final_deadline",
        headerName: resolveSiHeader(
          submittalHeaderMap,
          "Final Deadline",
          SI_SUBMITTAL_RENAME_KEY.final_deadline
        ),
        width: 160,
        headerTooltip: "As per New Schedule",
        valueFormatter: dateValueFormatter,
        filter: CustomDateSetFilter
      }
    ],
    [submittalHeaderMap]
  );

  const expandCollapseAll = useCallback(() => {
    let tmpExpandedItemIds: string[] = [];
    if (expandedItemIds.length && expandedItemIds.length === rowIds.length) {
      tmpExpandedItemIds = [];
    } else {
      tmpExpandedItemIds = rowIds;
    }

    gridRef?.current?.api?.forEachNode((node: any) => {
      node.expanded = !!tmpExpandedItemIds.length;
    });
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();
    setExpandedItemIds(tmpExpandedItemIds);
  }, [expandedItemIds.length, rowIds, setExpandedItemIds]);

  const exportToExcel = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.exportDataAsExcel({
      fileName: buildExportFileName(
        "impacted-submittals",
        projectDetails?.name
      ),
      sheetName: "Impacted Submittals",
      processCellCallback: scheduleImpactProcessCell,
      columnKeys: getVisibleExportColumnKeys(
        api,
        SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_SUBMITTAL,
        ALWAYS_EXPORT_KEYS_SUBMITTAL
      ),
      skipColumnHeaders: false,
      skipRowGroups: true
    });
  }, [projectDetails?.name]);

  const openPrintView = useCallback(
    (autoprint = false) => {
      if (!importLogId || !sourceLogId || !projectId) return;
      const api = gridRef.current?.api;
      // Prefer the filter+sort iterator (so the print view rows mirror
      // the grid's current display order); fall back to filter-only and
      // finally to the local risk-category-filtered data if the api
      // method isn't available.
      const filteredIds: string[] = [];
      let collected = false;
      if (api) {
        try {
          if (
            typeof (api as any).forEachNodeAfterFilterAndSort === "function"
          ) {
            (api as any).forEachNodeAfterFilterAndSort((node: any) => {
              if (node?.data?.id) filteredIds.push(node.data.id);
            });
            collected = true;
          } else if (typeof api.forEachNodeAfterFilter === "function") {
            api.forEachNodeAfterFilter((node: any) => {
              if (node?.data?.id) filteredIds.push(node.data.id);
            });
            collected = true;
          }
        } catch {
          collected = false;
        }
      }
      if (!collected) {
        filteredIds.push(...filteredRowData.map((r) => r.id));
      }
      // Capture the toggleable column ids that are currently visible so
      // the print view's table mirrors what the user sees in the grid.
      // Always-hidden columns (delta, risk_assessment, etc.) report
      // isVisible() === false and are naturally excluded.
      const visibleColumnIds =
        api
          ?.getColumns()
          ?.filter((c: any) => c.isVisible())
          .map((c: any) => c.getColId()) ?? undefined;
      const params: Record<string, string> = {
        importLogId,
        sourceLogId,
        tab: "submittals"
      };
      if (autoprint) params.autoprint = "1";
      const qs = new URLSearchParams(params).toString();
      // Filter payload travels in the URL fragment so it survives any
      // storage isolation (incognito, strict privacy mode, partitioned
      // localStorage). Server never sees `#`-text.
      const hash = buildPrintPayloadHash({
        importLogId,
        sourceLogId,
        tab: "submittals",
        filteredIds,
        sourceScheduleName,
        importScheduleName,
        visibleColumnIds
      });
      window.open(
        `/project/${projectId}/schedule/change-impact/printview?${qs}#${hash}`,
        "_blank",
        "noopener"
      );
    },
    [
      importLogId,
      sourceLogId,
      projectId,
      filteredRowData,
      sourceScheduleName,
      importScheduleName
    ]
  );

  const detailCellRenderer = useMemo(
    () => SubmittalImpactDetailCellRenderer,
    []
  );

  const gridContext = useMemo(
    () => ({
      projectId,
      projectParticipants,
      isIntegrationMode,
      isLoading
    }),
    [projectId, projectParticipants, isIntegrationMode, isLoading]
  );

  const getRowId = useCallback((params: any) => params.data.id, []);

  const handleFirstDataRendered = useCallback(
    (event: any) => {
      if (printMode) {
        event.api.forEachNode((node: any) => {
          node.setExpanded(true);
        });
        return;
      }
      // Restore saved expansion from sessionStorage (survives tab switch +
      // reload). expandedRef is used instead of the state value so this
      // callback stays stable across toggles.
      const saved = expandedRef.current;
      if (saved.length === 0) return;
      const ids = new Set(saved);
      event.api.forEachNode((node: any) => {
        if (node?.data?.id && ids.has(node.data.id)) node.setExpanded(true);
      });
    },
    [printMode, expandedRef]
  );

  const handleRowGroupOpened = useCallback(
    (event: any) => {
      if (event?.data?.id) toggleExpanded(event.data.id);
    },
    [toggleExpanded]
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        }
      ],
      defaultToolPanel: ""
    }),
    []
  );

  return (
    <div data-testid="impacted-submittals-grid" className="flex flex-col">
      {!printMode && (
        <div className="pb-2 pt-6 px-4 flex items-center justify-between hide-on-print">
          <Radio.Group
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {RISK_CATEGORIES.map((cat) => {
              const count = categoryCounts[cat.key] || 0;
              return (
                <Radio.Button key={cat.key} value={cat.key}>
                  <span className="inline-flex items-center gap-1.5">
                    {cat.label}
                    <span className="border border-solid border-ciq-gray-300 rounded-xl px-1.5 text-xs font-normal">
                      {count}
                    </span>
                  </span>
                </Radio.Button>
              );
            })}
          </Radio.Group>
          <div className="flex items-center gap-x-3">
            <CiqAgSearch
              key="ImpactedSubmittals_search"
              gridRef={gridRef}
              placeholder="Search Submittals"
            />
            <Button
              onClick={expandCollapseAll}
              className="w-[110px]"
              disabled={!filteredRowData.length}
            >
              {expandedItemIds.length &&
              expandedItemIds.length === rowIds.length ? (
                <span>Collapse All</span>
              ) : (
                <span>Expand All</span>
              )}
            </Button>
            <Dropdown
              placement="bottomRight"
              disabled={!filteredRowData.length || !!isProjectArchived}
              menu={{
                items: [
                  {
                    key: "excel",
                    label: (
                      <span data-testid="impacted-submittals-excel">
                        Export as Excel
                      </span>
                    ),
                    onClick: exportToExcel
                  },
                  {
                    key: "pdf",
                    disabled: !importLogId || !sourceLogId,
                    label: (
                      <span data-testid="impacted-submittals-pdf">
                        Export as PDF
                      </span>
                    ),
                    onClick: () => {
                      // Use AG Grid's displayed count when available so the
                      // launcher modal mirrors what the user actually sees;
                      // filteredRowData ignores active column filters.
                      const api = gridRef.current?.api;
                      const visibleCount =
                        api?.getDisplayedRowCount?.() ?? filteredRowData.length;
                      launchPrintView(() => openPrintView(true), visibleCount);
                    }
                  }
                ]
              }}
            >
              <Button
                disabled={!filteredRowData.length || !!isProjectArchived}
                data-testid="impacted-submittals-export"
              >
                Export <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
      )}
      <div style={{ height: printMode ? "auto" : "calc(100vh - 166px)" }}>
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={filteredRowData}
          getRowId={getRowId}
          rowHeight={48}
          headerHeight={36}
          tooltipShowDelay={300}
          pagination={false}
          suppressPaginationPanel
          suppressContextMenu
          masterDetail
          detailCellRenderer={detailCellRenderer}
          detailRowAutoHeight
          detailRowHeight={500}
          keepDetailRows={!!printMode}
          context={gridContext}
          sideBar={printMode ? undefined : sideBar}
          domLayout={printMode ? "print" : "normal"}
          suppressRowVirtualisation={!!printMode}
          suppressColumnVirtualisation={!!printMode}
          suppressMaxRenderedRowRestriction={!!printMode}
          onFirstDataRendered={handleFirstDataRendered}
          onRowGroupOpened={handleRowGroupOpened}
          noRowsOverlayComponent={SubmittalsNoRowsOverlay}
        />
      </div>
    </div>
  );
}

export default ImpactedSubmittalsGrid;
